import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useDebouncedValue } from '@/hooks/common';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Extra text matched by the search box but not shown (e.g. client_id, short_code). */
  keywords?: string;
}

interface SearchableSelectBaseProps {
  value: string;
  /**
   * The picked option is passed alongside its value so a caller can keep the
   * label without holding the list it came from — in server mode the option can
   * fall out of `options` on the very next keystroke.
   */
  onChange: (value: string, option?: SearchableSelectOption) => void;
  options: SearchableSelectOption[];
  /**
   * Label for the current `value` when it isn't among `options`. Keeps the
   * trigger from going blank for an out-of-list selection.
   */
  selectedLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Forwarded to the trigger button so callers can target it in tests. */
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    'data-testid'?: string;
  };
  /**
   * A fixed row rendered above the list and outside its loading/empty/error
   * states — for a clear entry ("All …"), which must stay reachable even when
   * the search behind it is in flight or failed.
   */
  leadingOption?: SearchableSelectOption;
  /** Replaces the trigger's label and chevron, for a menu button rather than a select box. */
  triggerContent?: React.ReactNode;
  /** Replaces the trigger's select-box classes. */
  triggerClassName?: string;
  /** Replaces the menu's placement, width and border colour (`mt-1 w-full border-gray-300`). */
  menuClassName?: string;
}

/** Default mode: the caller ships the whole list, the component filters it locally. */
interface LocalFilteringProps {
  onSearchChange?: undefined;
  isLoading?: undefined;
  isError?: undefined;
  hasMore?: undefined;
  onLoadMore?: undefined;
  isLoadingMore?: undefined;
}

/**
 * Server mode (LR-572). `isLoading` and `isError` are REQUIRED here, not a nicety:
 * an in-flight or failed search leaves `options` empty, and without those two the
 * list renders the affirmative "No results found" over it — the exact lie LR-572
 * was filed about. The union keeps the mode from being switched on halfway.
 */
interface ServerSearchProps {
  /**
   * Switches the component to server mode: local filtering is off and the
   * debounced query is reported here instead, for the caller to fetch with.
   * Called on query changes only, so an inline arrow is safe to pass.
   */
  onSearchChange: (query: string) => void;
  /** First page in flight. */
  isLoading: boolean;
  /** The search failed — the third state the list needs. */
  isError: boolean;
  /** More pages exist for the current query. */
  hasMore?: boolean;
  /** Called when the list is scrolled close to its end. */
  onLoadMore?: () => void;
  /** A further page is in flight — shown as a footer row, list stays usable. */
  isLoadingMore?: boolean;
}

type SearchableSelectProps = SearchableSelectBaseProps & (LocalFilteringProps | ServerSearchProps);

/**
 * Single-select dropdown with an in-list search box, built for registry fields
 * (counterparty, custodian, ...) whose option lists can be long.
 *
 * Two modes:
 * - default — the caller passes the full (already-loaded) option list and the
 *   component filters it locally, debounced so typing stays smooth.
 * - server (`onSearchChange` set) — the component owns the search box and
 *   reports the debounced query; the caller searches, pages, and feeds `options`
 *   back. For registries too large to ship to the browser at all.
 *
 * The selected label always renders on the trigger even if it scrolled out of
 * the filtered view, so a selection never shows as blank.
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  selectedLabel,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  disabled = false,
  className,
  buttonProps,
  onSearchChange,
  isLoading = false,
  isError = false,
  leadingOption,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  triggerContent,
  triggerClassName,
  menuClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 250);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isServerMode = onSearchChange !== undefined;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset the query each time the dropdown closes so the next open starts fresh.
  useEffect(() => {
    if (!isOpen) setSearchQuery('');
  }, [isOpen]);

  // Kept in a ref so the report below fires on query changes and nothing else:
  // depending on the callback identity would re-report on every parent render,
  // and a caller passing an inline arrow that sets non-primitive state would
  // loop. Declared before the reporting effect so it is current when that runs.
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });

  // Server mode: hand the debounced query to the caller. Reported on mount as
  // well (empty query = unfiltered first page), so the list is already there when
  // the dropdown is first opened instead of loading under the user.
  useEffect(() => {
    onSearchChangeRef.current?.(debouncedQuery.trim());
  }, [debouncedQuery]);

  const resolvedSelectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? selectedLabel,
    [options, value, selectedLabel]
  );

  const filteredOptions = useMemo(() => {
    // Server mode: `options` IS the answer to the current query. Filtering it
    // again locally would hide rows the server matched on fields the option does
    // not carry.
    if (isServerMode) return options;
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.keywords ?? '').toLowerCase().includes(q)
    );
  }, [options, debouncedQuery, isServerMode]);

  const selectOption = (option: SearchableSelectOption) => {
    onChange(option.value, option);
    setIsOpen(false);
  };

  const renderOption = (option: SearchableSelectOption) => (
    <button
      type="button"
      key={option.value}
      onClick={() => selectOption(option)}
      className={clsx(
        'w-full text-left flex items-center px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer',
        option.value === value ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
      )}
    >
      <span className="truncate" title={option.label}>
        {option.label}
      </span>
    </button>
  );

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoadingMore) return;
    const el = event.currentTarget;
    // Roughly one row of slack, so the next page is already on its way by the
    // time the user reaches the bottom.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) onLoadMore?.();
  };

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      <button
        {...buttonProps}
        type="button"
        aria-expanded={isOpen}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        disabled={disabled}
        className={
          triggerClassName ??
          clsx(
            'w-full flex items-center justify-between px-2 py-1 text-sm border rounded bg-white',
            'focus:outline-none focus:ring-blue-500 focus:border-blue-500',
            disabled
              ? 'bg-gray-50 cursor-not-allowed text-gray-400 border-gray-300'
              : 'hover:bg-gray-50',
            isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
          )
        }
      >
        {triggerContent ?? (
          <>
            <span
              className={clsx('truncate', resolvedSelectedLabel ? 'text-gray-900' : 'text-gray-400')}
              title={resolvedSelectedLabel}
            >
              {resolvedSelectedLabel || placeholder}
            </span>
            <ChevronDownIcon
              className={clsx(
                'h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ml-2',
                isOpen && 'transform rotate-180'
              )}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 bg-white border rounded-md shadow-lg max-h-[300px] flex flex-col',
            menuClassName ?? 'mt-1 w-full border-gray-300'
          )}
        >
          <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  // Escape closes only this dropdown — stop it bubbling to a
                  // parent modal that also listens for Escape.
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setIsOpen(false);
                  }
                }}
              />
            </div>
          </div>

          <div
            className="overflow-y-auto flex-1"
            onScroll={handleScroll}
            data-testid="searchable-select-list"
            aria-busy={isLoading || isLoadingMore}
          >
            {/* Outside the state switch below: a clear entry that vanishes while
                the list loads or fails would leave the filter unclearable. */}
            {leadingOption && renderOption(leadingOption)}

            {isLoading ? (
              // Distinct from `emptyMessage`: "still fetching" must never render as
              // "there is nothing" (LR-572).
              <div className="px-3 py-2 text-sm text-gray-500 text-center" role="status">
                Loading…
              </div>
            ) : filteredOptions.length === 0 ? (
              // A failed request also leaves the list empty, and saying "nothing
              // matched" for it is the same lie in a stickier form.
              isError ? (
                <div className="px-3 py-2 text-sm text-red-600 text-center" role="alert">
                  Search failed — try again
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">{emptyMessage}</div>
              )
            ) : (
              <>
                {filteredOptions.map(renderOption)}
                {isLoadingMore && (
                  <div className="px-3 py-2 text-sm text-gray-500 text-center" role="status">
                    Loading more…
                  </div>
                )}
                {/* A page that failed is a footer, never a replacement: the rows
                    already loaded stay pickable. */}
                {isError && !isLoadingMore && (
                  <div className="px-3 py-2 text-sm text-red-600 text-center" role="alert">
                    Could not load more
                  </div>
                )}
                {/* The scroll handler is a convenience; this is the operable one,
                    since a keyboard or screen-reader user never triggers a scroll. */}
                {hasMore && !isLoadingMore && (
                  <button
                    type="button"
                    onClick={() => onLoadMore?.()}
                    className="w-full px-3 py-2 text-sm text-blue-700 hover:bg-gray-50"
                  >
                    Load more
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
